/**
 * LE CSS PROPRE À UN ÉVÉNEMENT — celui que l'organisateur demande, bloc par bloc.
 *
 * Porté de `fo-meeting/src/utils/cssMerge.js`, à l'identique pour la découpe et la
 * fusion : c'est le format que la V1 lit et écrit depuis des mois, et le modèle côté
 * serveur rend du CSS écrit contre lui.
 *
 * ⚠️ CE QUI EST STOCKÉ N'EST PAS CE QUI EST APPLIQUÉ, et c'est délibéré. Le réglage
 * garde le sélecteur NU — `[data-cfg="infoTitle"] h2 { … }` — lisible, portable,
 * relisible dans un diff. Le confinement à `.site` est posé au dernier moment, par
 * `pourInjection`. Voir pourquoi juste en dessous.
 */

export type Rule = { selector: string; body: string };

const normalise = (s: string) => s.replace(/\s+/g, " ").trim();

/**
 * DÉCOUPER UNE FEUILLE EN RÈGLES.
 *
 * ⚠️ LA PROFONDEUR EST COMPTÉE, ET IL LE FAUT. Une `@media` contient des accolades :
 * s'arrêter à la première fermante couperait la règle en deux, et la seconde moitié
 * deviendrait un sélecteur bâtard qui ne correspond à rien. C'est aussi ce qui permet
 * à `mergeCss` de reconnaître une media query comme UNE règle, donc de la remplacer.
 */
export function parseRules(css: string): Rule[] {
  if (!css) return [];
  const rules: Rule[] = [];
  let depth = 0;
  let buffer = "";
  let selector = "";

  for (const c of css) {
    if (c === "{" && depth === 0) {
      selector = buffer.trim();
      buffer = "";
      depth = 1;
    } else if (c === "{") {
      depth += 1;
      buffer += c;
    } else if (c === "}" && depth === 1) {
      if (selector) rules.push({ selector, body: buffer.trim() });
      buffer = "";
      selector = "";
      depth = 0;
    } else if (c === "}") {
      depth -= 1;
      buffer += c;
    } else {
      buffer += c;
    }
  }
  return rules;
}

export const stringifyRules = (rules: readonly Rule[]): string =>
  rules.map((r) => `${r.selector} {\n  ${r.body}\n}`).join("\n\n");

/**
 * FUSIONNER — PAR SÉLECTEUR, ET NON EN AJOUTANT.
 *
 * ⚠️ UNE RÈGLE ENTRANTE REMPLACE CELLE DE MÊME SÉLECTEUR. Sans ça, demander trois fois
 * de suite « plus grand » sur le même bloc empilerait trois règles contradictoires dont
 * seule la dernière compte : la feuille grossirait à chaque essai, personne ne saurait
 * plus laquelle agit, et rien ne serait jamais retiré.
 *
 * Une feuille entrante vide ne fait rien : le modèle qui n'a rien su produire ne doit
 * pas effacer ce qui existait.
 */
export function mergeCss(existing: string, incoming: string): string {
  const fresh = parseRules(incoming);
  if (fresh.length === 0) return existing || "";

  const replaced = new Set(fresh.map((r) => normalise(r.selector)));
  const kept = parseRules(existing).filter(
    (r) => !replaced.has(normalise(r.selector)),
  );
  return stringifyRules([...kept, ...fresh]);
}

/** Retirer une règle, par son sélecteur. Les autres ne bougent pas. */
/**
 * LES RÈGLES QUI VISENT CET ÉLÉMENT — le sélecteur exact, et ceux qui le prolongent.
 *
 * ⚠️ « PROLONGENT », PARCE QU'UNE DEMANDE EN PRODUIT PLUSIEURS. « Mets le fond en
 * rouge » sur un bloc a donné trois règles sur un vrai événement : le bloc, ses
 * `::before`/`::after`, ses enfants (`> *`). Elles forment UN réglage aux yeux de
 * l'organisateur ; c'est cet ensemble que la modale montre, donne au modèle, et
 * remplace. Le bloc parent, lui, n'est pas concerné quand on vise un enfant.
 */
export function rulesFor(css: string, selector: string): Rule[] {
  const target = normalise(selector);
  return parseRules(css).filter((r) => normalise(r.selector).includes(target));
}

/**
 * REMPLACE UN ENSEMBLE DE RÈGLES PAR LA PROPOSITION — et pas seulement celles dont le
 * sélecteur est identique.
 *
 * ⚠️ C'ÉTAIT LE DÉFAUT DE LA SECONDE DEMANDE. `mergeCss` seul ne remplace qu'à sélecteur
 * égal : quand le modèle ne réémettait pas les règles annexes de la première demande
 * (`::before`, `> *`…), elles restaient, et continuaient d'agir. La seconde
 * modification semblait ne rien changer, ou changer à moitié — y compris après
 * rechargement, puisque c'était bien ce qui était enregistré. On retire d'abord tout
 * ce qui a été montré à l'organisateur, puis on fusionne : ce qu'il a lu dans la
 * modale est exactement ce qui disparaît.
 */
export function replaceRules(
  existing: string,
  replaced: readonly string[],
  incoming: string,
): string {
  const gone = new Set(replaced.map(normalise));
  const kept = stringifyRules(
    parseRules(existing).filter((r) => !gone.has(normalise(r.selector))),
  );
  return mergeCss(kept, incoming);
}

export function withoutRule(css: string, selector: string): string {
  const target = normalise(selector);
  return stringifyRules(
    parseRules(css).filter((r) => normalise(r.selector) !== target),
  );
}

/**
 * ── LE GARDE-FOU ──
 *
 * ⚠️ LE PANNEAU DE L'ORGANISATEUR VIT DANS LE MÊME DOCUMENT QUE LE SITE. Une feuille
 * posée telle quelle dans le `<head>` l'atteint aussi : `button { display: none }`
 * suffirait à effacer les boutons de l'OUTIL avec lequel on est en train de travailler
 * — y compris celui qui aurait permis de retirer la règle. On se retrouverait enfermé
 * dehors, sans autre issue qu'une intervention en base.
 *
 * Chaque sélecteur est donc confiné sous `.site`, qui enveloppe le site et rien
 * d'autre. Y compris — surtout — ceux qui ne visent aucun bloc nommé.
 *
 * ⚠️ ET LE `@media` SE PRÉFIXE À L'INTÉRIEUR, PAS DEVANT. `.site @media …` n'est pas du
 * CSS : la règle entière serait ignorée, et le confinement avec elle. Ce qu'il faut
 * préfixer, ce sont les sélecteurs QU'ELLE CONTIENT.
 */
export function forInjection(css: string): string {
  return parseRules(css)
    .map((r) => {
      const s = r.selector.trim();
      if (s.startsWith("@")) {
        // Une règle à parenthèse (`@media`, `@supports`) : on confine son contenu.
        // ⚠️ `@font-face`, `@page` n'ont PAS de règles imbriquées : leur corps est une
        // liste de déclarations, que `parseRules` rend vide. On le garde tel quel,
        // sinon une police déclarée par le client disparaissait en silence.
        const nested = parseRules(r.body);
        if (nested.length === 0) return `${s} {\n  ${r.body}\n}`;
        return `${s} {\n${forInjection(r.body)}\n}`;
      }
      return `${confine(s)} {\n  ${r.body}\n}`;
    })
    .join("\n\n");
}

/** `a, b` → `.site a, .site b` : chaque branche compte, pas seulement la première. */
function confine(selector: string): string {
  return selector
    .split(",")
    .map((part) => {
      const p = part.trim();
      if (!p) return p;
      // Déjà confiné : ne pas empiler `.site .site`.
      return p === ".site" || p.startsWith(".site ") ? p : `.site ${p}`;
    })
    .filter(Boolean)
    .join(", ");
}
